"use client";

import { useRef, useCallback, useEffect, useState } from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import Link from "next/link";
import { X, ChevronDown } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

import { Logo } from "@/components/typography/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRightThin } from "@/components/icons";
import { FeedbackButtonIcon } from "@/components/layout/FeedbackButton";
import { secondaryLinks, SOCIAL_LINKS } from "@/lib/nav-data";
import { useNavGroups } from "./NavHubsContext";
import {
  SignedIn,
  SignedOut,
  ClerkLoaded,
} from "@clerk/nextjs";
import { useAuthModal } from "@/components/auth/AuthModalContext";

type MobileNavPanelProps = {
  open: boolean;
  onClose: () => void;
};

const DISMISS_THRESHOLD = 100;

const MobileNavPanel = ({ open, onClose }: MobileNavPanelProps) => {
  const { setOpen: openAuthModal } = useAuthModal();
  const navGroups = useNavGroups();
  /** Welcher Unterpunkt mit dritter Ebene aufgeklappt ist. */
  const [openSubItem, setOpenSubItem] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);

  const closeMethodRef = useRef<"close_button" | "drag" | "overlay" | null>(null);
  const closeFiredRef = useRef(false);

  // Reset close tracking and the third-level accordion when panel re-opens
  useEffect(() => {
    if (open) {
      closeFiredRef.current = false;
      setOpenSubItem(null);
    }
  }, [open]);

  const trackClose = useCallback(
    (method: "close_button" | "drag" | "overlay") => {
      if (closeFiredRef.current) return;
      closeFiredRef.current = true;
      trackEvent("mobile_menu_closed", { method });
    },
    []
  );

  const trackLink = useCallback(
    (label: string, href: string, section: string) => {
      trackEvent("mobile_menu_link_clicked", { label, href, section });
    },
    []
  );

  // Reset inline styles when panel re-opens
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.style.transform = "";
      contentRef.current.style.transition = "";
    }
  }, [open]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Only allow drag-to-dismiss when scroll is at the top
    if (navRef.current && navRef.current.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    currentY.current = 0;
    isDragging.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (navRef.current && navRef.current.scrollTop > 0) return;
    const deltaY = e.touches[0].clientY - startY.current;
    // Only track downward drags
    if (deltaY <= 0) {
      if (isDragging.current) {
        // Reset if user drags back up
        isDragging.current = false;
        currentY.current = 0;
        if (contentRef.current) {
          contentRef.current.style.transform = "";
          contentRef.current.style.transition = "";
        }
      }
      return;
    }
    isDragging.current = true;
    currentY.current = deltaY;
    if (contentRef.current) {
      contentRef.current.style.transition = "none";
      contentRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (contentRef.current) {
      if (currentY.current > DISMISS_THRESHOLD) {
        // Animate out then close
        trackClose("drag");
        contentRef.current.style.transition = "transform 250ms ease-out";
        contentRef.current.style.transform = "translateY(100%)";
        setTimeout(onClose, 250);
      } else {
        // Snap back
        contentRef.current.style.transition = "transform 200ms ease-out";
        contentRef.current.style.transform = "";
      }
    }
    currentY.current = 0;
  }, [onClose, trackClose]);

  return (
    <SheetPrimitive.Root open={open} onOpenChange={(o) => { if (!o) { trackClose(closeMethodRef.current || "overlay"); closeMethodRef.current = null; onClose(); } }}>
      <SheetPrimitive.Portal>
        {/* Transparent overlay — no dark backdrop in the design */}
        <SheetPrimitive.Overlay className="fixed inset-0 z-50" />

        <SheetPrimitive.Content
          ref={contentRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="fixed inset-x-0 bottom-0 z-50 bg-sun-100 rounded-t-[32px] h-[calc(100dvh-24px)] p-0 flex flex-col
                     data-[state=open]:animate-in data-[state=closed]:animate-out
                     data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom
                     data-[state=closed]:duration-300 data-[state=open]:duration-500"
        >
          {/* Accessible title for screen readers */}
          <SheetPrimitive.Title className="sr-only">
            Navigation
          </SheetPrimitive.Title>

          {/* Drag handle indicator */}
          <div className="flex justify-center pt-3 pb-0 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-black/20" />
          </div>

          {/* Logo (left) + Close (right) */}
          <div className="flex items-center justify-between px-6 pt-3 pb-4 flex-shrink-0">
            <Link href="/" onClick={() => { trackLink("Dialetics", "/", "logo"); onClose(); }} aria-label="Dialetics">
              <Logo className="h-8 w-auto" />
            </Link>
            <SheetPrimitive.Close asChild>
              <button
                onPointerDown={() => { closeMethodRef.current = "close_button"; }}
                className="size-8 rounded-full bg-sun-75 flex items-center justify-center"
                aria-label="Menü schließen"
              >
                <X className="size-5 text-black" />
              </button>
            </SheetPrimitive.Close>
          </div>

          {/* Nav + bottom bar wrapper — relative so the bar can overlap the scroll area */}
          <div className="flex-1 relative overflow-hidden">
            {/* Scrollable nav sections */}
            <nav ref={navRef} className="h-full overflow-y-auto px-6 pb-24">
              <div className="flex flex-col gap-5">
                {/* Primary nav groups */}
                {navGroups.map((group) => (
                  <div key={group.key} className="flex flex-col gap-2">
                    {group.subItems.length > 0 ? (
                      <>
                        <span className="text-copy-sm opacity-60">
                          {group.label}
                        </span>
                        {group.subItems.map((item) =>
                          item.children ? (
                            // Dritte Ebene (Produkt Reviews) als Aufklapper.
                            // Abstaende per Rand statt `gap` (iOS 14).
                            <div key={item.href}>
                              <button
                                type="button"
                                aria-expanded={openSubItem === item.href}
                                onClick={() => setOpenSubItem((cur) => (cur === item.href ? null : item.href))}
                                className="text-link-lg text-black flex items-center"
                              >
                                {item.label}
                                <ChevronDown
                                  className={`size-5 ml-2 transition-transform ${openSubItem === item.href ? "rotate-180" : ""}`}
                                />
                              </button>
                              {openSubItem === item.href && (
                                <div className="flex flex-col pl-4 mt-2 [&>*+*]:mt-2">
                                  {item.children.map((child) => (
                                    <Link
                                      key={child.href}
                                      href={child.href}
                                      onClick={() => { trackLink(child.label, child.href, group.key); onClose(); }}
                                      className="text-copy text-black"
                                    >
                                      {child.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => { trackLink(item.label, item.href, group.key); onClose(); }}
                              className="text-link-lg text-black"
                            >
                              {item.label}
                            </Link>
                          )
                        )}
                      </>
                    ) : (
                      <Link
                        href={group.href}
                        onClick={() => { trackLink(group.label, group.href, group.key); onClose(); }}
                        className="text-link-lg text-black"
                      >
                        {group.label}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Company section */}
                <div className="flex flex-col gap-2">
                  <span className="text-copy-sm opacity-60">Company</span>
                  {secondaryLinks.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => { trackLink(item.label, item.href, "company"); onClose(); }}
                      className="text-link-lg text-black"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>

                {/* Social section */}
                <div className="flex flex-col gap-2">
                  <span className="text-copy-sm opacity-60">Social</span>
                  <div className="flex gap-2">
                    {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        onClick={() => trackLink(label, href, "social")}
                      >
                        <Icon className="size-9 text-black" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* Bottom bar — absolutely positioned to overlap scroll content for blur effect */}
            <div className="absolute bottom-0 left-0 right-0 px-4 pb-6 pt-6 backdrop-blur-[2px]">
              <div className="flex items-center gap-2">
                <ClerkLoaded>
                  <SignedIn>
                    <Button variant="black" size="lg" asChild className="flex-1">
                      <Link href="/dashboard" onClick={() => { trackLink("Dashboard", "/dashboard", "cta"); onClose(); }}>
                        Dashboard
                        <ArrowRightThin className="ml-2 size-5" />
                      </Link>
                    </Button>
                  </SignedIn>
                  <SignedOut>
                    <Button
                      variant="black"
                      size="lg"
                      className="flex-1"
                      asChild
                    >
                      {/* Getrackt wird weiter "Jetzt anmelden", obwohl der Knopf seit
                          10/2026 "Anmelden" heisst: PostHog-/GA4-Auswertungen filtern
                          auf dieses Label und fielen sonst ab dem Umbenennen auf null. */}
                      <Link href="/login" onClick={() => { trackLink("Jetzt anmelden", "/login", "cta"); onClose(); }}>
                        Anmelden
                        <ArrowRightThin className="ml-2 size-5" />
                      </Link>
                    </Button>
                  </SignedOut>
                </ClerkLoaded>

                {/* Feedback button — closes nav so Tally modal isn't blocked by Radix focus trap */}
                <FeedbackButtonIcon onPress={onClose} />
              </div>
            </div>
          </div>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  );
};

export { MobileNavPanel };
